/**
 * HQ 콘솔 무동작(idle) 자동 로그아웃 훅.
 *
 * 보안 강화(2026-07-04): 본부 콘솔은 여러 매장의 민감 데이터를 다루므로
 * 공용/공유 PC에서 로그아웃 없이 자리를 비우는 상황을 방지한다.
 * 일정 시간(기본 30분) 사용자 입력이 없으면 토큰을 지우고 로그인 화면으로 보낸다.
 *
 * ※ 전역 JWT 만료시간(core/auth)은 오너 앱과 공유되므로 건드리지 않고,
 *   HQ 세션에만 적용되는 클라이언트 측 유휴 타임아웃으로 구현.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { hqAuthService } from '../services/hqAuthService';
import { HQ_BASE } from '../constants';

const DEFAULT_IDLE_MS = 30 * 60 * 1000; // 30분
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function useHqIdleLogout(idleMs: number = DEFAULT_IDLE_MS): void {
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const logout = () => {
      hqAuthService.logout();
      try {
        window.alert('장시간 사용하지 않아 보안을 위해 자동 로그아웃되었습니다. 다시 로그인해 주세요.');
      } catch {
        /* alert 불가 환경 무시 */
      }
      navigate(`${HQ_BASE}/login`, { replace: true });
    };

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, idleMs);
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
    reset(); // 최초 타이머 시작

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, reset));
    };
  }, [idleMs, navigate]);
}

export default useHqIdleLogout;
